import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { errorMessage, reportError } from '../../lib/errors';
import toast from 'react-hot-toast';

export const DangerZoneSection = () => {
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [orgName, setOrgName] = useState<string>('');
    const [confirmationText, setConfirmationText] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        const fetchOrg = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'danger-zone-section.profiles');

            if (!profile?.active_org_id) return;
            setOrgId(profile.active_org_id);

            const { data, error: fetchError } = await supabase
                .from('organizations')
                .select('name')
                .eq('id', profile.active_org_id)
                .single();
            if (fetchError) reportError(fetchError, 'danger-zone-section.organizations');

            if (data) {
                setOrgName(data.name || '');
            }
            setLoading(false);
        };

        fetchOrg();
    }, []);

    const handleDelete = async () => {
        if (confirmationText !== orgName || !orgId) return;

        setDeleting(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error("Authentication session missing. Please log in again.");

            const { data, error } = await supabase.functions.invoke('delete-organization', {
                body: { orgId },
                headers: {
                    Authorization: `Bearer ${session.access_token}`
                }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            if (!data?.accepted || !data?.request_id) throw new Error('Erasure request was not recorded.');
            toast.success('Erasure request recorded. Support will review retention and contact you.');

        } catch (error) {
            console.error('Failed to request workspace erasure:', error);
            toast.error(errorMessage(error) || 'Failed to request workspace erasure. Please try again.');
        } finally {
            setDeleting(false);
            setShowConfirm(false);
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-fg-4" /></div>;

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-danger flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Danger Zone
                </h3>
                <p className="text-sm text-fg-2">Workspace closure and data retention requests.</p>
            </div>

            <div className="bg-danger/10 border border-danger/20 rounded-[var(--fds-radius)] p-6 space-y-4">
                <div>
                    <h4 className="font-bold text-fg">Request Workspace Erasure</h4>
                    <p className="text-sm text-fg-2">
                        Request closure and erasure of this workspace. Financial records may need to be retained. Your workspace stays available while support reviews the request and confirms what will be erased or retained.
                    </p>
                </div>

                {!showConfirm ? (
                    <button
                        onClick={() => setShowConfirm(true)}
                        className="bg-danger/20 hover:bg-danger/30 text-danger px-4 py-2 rounded-[var(--fds-radius)] font-medium transition-colors"
                    >
                        Request Workspace Erasure
                    </button>
                ) : (
                    <div className="bg-panel border border-danger/50 rounded-[var(--fds-radius)] p-6 space-y-4 mt-4">
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-fg">
                                To confirm, type <strong className="text-danger">{orgName}</strong> below:
                            </p>
                            <input
                                type="text"
                                value={confirmationText}
                                onChange={(e) => setConfirmationText(e.target.value)}
                                className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-2 text-fg focus:outline-none focus:ring-2 focus:ring-danger/50"
                                placeholder={orgName}
                            />
                        </div>
                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={handleDelete}
                                disabled={confirmationText !== orgName || deleting}
                                className="flex-1 bg-danger-fill text-on-fill hover:bg-danger-fill disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-[var(--fds-radius)] font-medium transition-colors flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Submit erasure request
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirm(false);
                                    setConfirmationText('');
                                }}
                                disabled={deleting}
                                className="flex-1 border border-line hover:bg-raised text-fg px-4 py-2 rounded-[var(--fds-radius)] font-medium transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
